import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

interface ClientInfo {
  id: string;
  name: string;
}

// Persistent state interface (what gets saved to localStorage)
interface PersistentState {
  selectedClientId: string | null;
  selectedClientIds: string[];
  selectedClientNames: Record<string, string>;
  hasInitializedClients: boolean;
}

// Non-persistent state interface
interface NonPersistentState {
  // UI State
  isSidebarOpen: boolean;
  toggleSidebar: () => void;

  // Theme
  theme: "light" | "dark";
  setTheme: (theme: "light" | "dark") => void;

  // User State
  user: {
    id: string | null;
    name: string | null;
    email: string | null;
  } | null;
  setUser: (user: AppState["user"]) => void;
  clearUser: () => void;

  // Date Range Selection
  dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  };
  setDateRange: (dateRange: {
    startDate: Date | null;
    endDate: Date | null;
  }) => void;

  // Initialize default date range (current month till today)
  initializeDefaultDateRange: () => void;
}

// Actions interface (methods that modify persistent state)
interface PersistentActions {
  setSelectedClientId: (clientId: string | null) => void;
  setSelectedClientIds: (clientIds: string[]) => void;
  setSelectedClientNames: (clientNames: Record<string, string>) => void;
  setHasInitializedClients: (initialized: boolean) => void;
  clearClientSelection: () => void;
  getSelectedClientNames: () => ClientInfo[];
  // Backward compatibility method (will be removed after migration)
  hydrateClients: () => void;
}

// Combined interface
interface AppState extends PersistentState, NonPersistentState, PersistentActions {}

// Helper function to get default date range (current year)
const getDefaultDateRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Today's date
  
  return {
    startDate: startOfMonth,
    endDate: today,
  };
};

// Legacy localStorage keys for backward compatibility migration
const LEGACY_CLIENT_STORAGE_KEYS = {
  SELECTED_CLIENT_ID: 'selectedClientId',
  SELECTED_CLIENT_IDS: 'selectedClientIds',
  SELECTED_CLIENT_NAMES: 'selectedClientNames',
  HAS_INITIALIZED_CLIENTS: 'hasInitializedClients'
} as const;

// Custom storage that handles migration from legacy localStorage keys
const createMigrationStorage = () => {
  return {
    getItem: (name: string) => {
      // First try to get from the new persist storage
      const newData = localStorage.getItem(name);
      if (newData) {
        return newData;
      }

      // If not found, try to migrate from legacy keys (one-time migration)
      try {
        const migratedData: Partial<PersistentState> = {};
        let hasMigrationData = false;

        // Migrate legacy data
        const legacyClientId = localStorage.getItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_ID);
        if (legacyClientId) {
          migratedData.selectedClientId = legacyClientId;
          hasMigrationData = true;
        }

        const legacyClientIds = localStorage.getItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_IDS);
        if (legacyClientIds) {
          try {
            const parsed = JSON.parse(legacyClientIds);
            if (Array.isArray(parsed)) {
              migratedData.selectedClientIds = parsed;
              hasMigrationData = true;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        const legacyClientNames = localStorage.getItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_NAMES);
        if (legacyClientNames) {
          try {
            const parsed = JSON.parse(legacyClientNames);
            if (typeof parsed === 'object' && parsed !== null) {
              migratedData.selectedClientNames = parsed;
              hasMigrationData = true;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        const legacyInitialized = localStorage.getItem(LEGACY_CLIENT_STORAGE_KEYS.HAS_INITIALIZED_CLIENTS);
        if (legacyInitialized === 'true') {
          migratedData.hasInitializedClients = true;
          hasMigrationData = true;
        }

        if (hasMigrationData) {
          // Create the new persist format
          const persistData = {
            state: migratedData,
            version: 0
          };
          const persistDataStr = JSON.stringify(persistData);
          
          // Save to new format
          localStorage.setItem(name, persistDataStr);
          
          // Clean up legacy keys
          localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_ID);
          localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_IDS);
          localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_NAMES);
          localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.HAS_INITIALIZED_CLIENTS);
          
          return persistDataStr;
        }
      } catch (error) {
        // Migration failed, clean up legacy keys
        localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_ID);
        localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_IDS);
        localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.SELECTED_CLIENT_NAMES);
        localStorage.removeItem(LEGACY_CLIENT_STORAGE_KEYS.HAS_INITIALIZED_CLIENTS);
      }

      return null;
    },
    setItem: (name: string, value: string) => {
      localStorage.setItem(name, value);
    },
    removeItem: (name: string) => {
      localStorage.removeItem(name);
    }
  };
};

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Non-persistent UI State
      isSidebarOpen: false,
      toggleSidebar: () =>
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      // Non-persistent Theme
      theme: "light",
      setTheme: (theme) => set({ theme }),

      // Non-persistent User State
      user: null,
      setUser: (user) => set({ user }),
      clearUser: () => {
        set({ user: null });
        // Also clear client selection and initialization state
        set({
          selectedClientId: null,
          selectedClientIds: [],
          selectedClientNames: {},
          hasInitializedClients: false,
        });
      },

      // Non-persistent Date Range Selection - Initialize with default values
      dateRange: getDefaultDateRange(),
      setDateRange: (dateRange) => set({ dateRange }),

      // Initialize default date range
      initializeDefaultDateRange: () => {
        const defaultRange = getDefaultDateRange();
        set({ dateRange: defaultRange });
      },

      // Persistent Client Selection State (automatically persisted by zustand)
      selectedClientId: null,
      setSelectedClientId: (clientId) => {
        set({ selectedClientId: clientId });
      },
      selectedClientIds: [],
      setSelectedClientIds: (clientIds) => {
        set({ selectedClientIds: clientIds });
      },

      // Persistent Client Names mapping (automatically persisted by zustand)
      selectedClientNames: {},
      setSelectedClientNames: (clientNames) => {
        set({ selectedClientNames: clientNames });
      },

      // Persistent Client Initialization State (automatically persisted by zustand)
      hasInitializedClients: false,
      setHasInitializedClients: (initialized) => {
        set({ hasInitializedClients: initialized });
      },

      // Clear client selection state (persisted automatically)
      clearClientSelection: () => {
        set({
          selectedClientId: null,
          selectedClientIds: [],
          selectedClientNames: {},
          hasInitializedClients: false,
        });
      },

      // Helper method to get selected client names as array
      getSelectedClientNames: () => {
        const state = get();
        return state.selectedClientIds.map((id) => ({
          id,
          name:
            (state.selectedClientNames && state.selectedClientNames[id]) ||
            `Client ${id}`
        }));
      },

      // Backward compatibility method (no-op since persist handles hydration)
      hydrateClients: () => {
        // This method is kept for backward compatibility but does nothing
        // as zustand persist middleware handles hydration automatically
      },
    }),
    {
      name: 'app-storage', // Keep the same storage key for backward compatibility
      storage: createJSONStorage(() => createMigrationStorage()),
      // Only persist client selection related state
      partialize: (state) => ({
        selectedClientId: state.selectedClientId,
        selectedClientIds: state.selectedClientIds,
        selectedClientNames: state.selectedClientNames,
        hasInitializedClients: state.hasInitializedClients,
      } as PersistentState),
    }
  )
);
